import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

/**
 * Stripe Checkout — return page.
 * Polls /api/billing/stripe/status/{session_id} until paid|expired|cancelled.
 */
export default function BillingSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const [state, setState] = useState({ phase: 'polling', data: null, error: null });

    const poll = useCallback(async (attempt = 0) => {
        if (!sessionId) {
            setState({ phase: 'error', error: 'Identifiant de session manquant', data: null });
            return;
        }
        if (attempt >= MAX_POLL_ATTEMPTS) {
            setState({ phase: 'timeout', error: null, data: null });
            return;
        }
        try {
            const r = await axios.get(`${API}/billing/stripe/status/${encodeURIComponent(sessionId)}`);
            const d = r.data || {};
            if (d.status === 'paid') {
                setState({ phase: 'paid', data: d, error: null });
                trackEvent('checkout_paid', { plan: d.plan_id, amount: d.amount, currency: d.currency });
                return;
            }
            if (d.status === 'expired' || d.status === 'cancelled' || d.status === 'failed') {
                setState({ phase: 'failed', data: d, error: null });
                return;
            }
            // 'pending' or 'initiated' — keep polling, but expose latest note
            setState({ phase: 'polling', data: d, error: null });
            setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
            setState({ phase: 'error', error: msg, data: null });
        }
    }, [sessionId]);

    useEffect(() => { poll(0); }, [poll]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="max-w-lg w-full card-soft p-10 text-center" data-testid="billing-success-page">
                {state.phase === 'polling' && (
                    <>
                        <Loader2 size={40} className="mx-auto animate-spin text-primary mb-5" />
                        <h1 className="font-heading text-2xl mb-2">Confirmation en cours…</h1>
                        <p className="text-foreground/65 text-sm">
                            {state.data?.note || 'Nous validons votre paiement Stripe. Cela prend quelques secondes.'}
                        </p>
                    </>
                )}

                {state.phase === 'paid' && state.data && state.data.plan_id === 'cart' && (
                    <>
                        <div className="h-16 w-16 rounded-full bg-primary-light text-primary mx-auto flex items-center justify-center mb-5">
                            <CheckCircle2 size={32} />
                        </div>
                        <h1 className="font-heading text-3xl mb-3" data-testid="billing-cart-paid-title">
                            Commande <span className="text-primary">confirmée</span> 🎉
                        </h1>
                        <p className="text-foreground/70 mb-2 leading-relaxed">
                            Votre paiement de <strong>{state.data.amount?.toLocaleString('fr-FR')} {state.data.currency}</strong>
                            {' '}a bien été enregistré. Vos packs sont activés.
                        </p>
                        <Link to="/app/orders">
                            <Button size="lg" className="w-full bg-primary hover:bg-primary-hover h-12 mt-4" data-testid="billing-cart-orders-cta">
                                Voir mes commandes <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </Link>
                        <Link to="/app/marketplace" className="block text-xs text-foreground/55 hover:text-foreground mt-5">
                            ← Retour à la Marketplace
                        </Link>
                    </>
                )}

                {state.phase === 'paid' && state.data && state.data.plan_id !== 'cart' && (
                    <>
                        <div className="h-16 w-16 rounded-full bg-primary-light text-primary mx-auto flex items-center justify-center mb-5">
                            <CheckCircle2 size={32} />
                        </div>
                        <h1 className="font-heading text-3xl mb-3" data-testid="billing-paid-title">
                            Bienvenue dans le plan <span className="text-primary">{state.data.plan_name}</span> 🎉
                        </h1>
                        <p className="text-foreground/70 mb-2 leading-relaxed">
                            Votre paiement de <strong>{state.data.amount?.toLocaleString('fr-FR')} {state.data.currency}</strong>
                            {' '}a bien été enregistré.
                        </p>
                        <p className="text-sm text-foreground/55 mb-6">
                            Un email de confirmation avec votre lien d'activation a été envoyé.
                        </p>
                        {state.data.activation_url && (
                            <a href={state.data.activation_url}>
                                <Button size="lg" className="w-full bg-primary hover:bg-primary-hover h-12" data-testid="billing-activate-cta">
                                    Activer mon espace African Valley <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </a>
                        )}
                        <Link to="/" className="block text-xs text-foreground/55 hover:text-foreground mt-5">
                            ← Retour à l'accueil
                        </Link>
                    </>
                )}

                {state.phase === 'failed' && (
                    <>
                        <div className="h-16 w-16 rounded-full bg-destructive/10 text-destructive mx-auto flex items-center justify-center mb-5">
                            <AlertCircle size={32} />
                        </div>
                        <h1 className="font-heading text-2xl mb-3">Paiement non finalisé</h1>
                        <p className="text-foreground/70 mb-6">
                            Votre session de paiement a expiré ou a été annulée. Aucune somme n'a été débitée.
                        </p>
                        <Link to="/#pricing">
                            <Button className="bg-primary hover:bg-primary-hover">Revenir aux tarifs</Button>
                        </Link>
                    </>
                )}

                {state.phase === 'timeout' && (
                    <>
                        <Loader2 size={32} className="mx-auto text-foreground/40 mb-5" />
                        <h1 className="font-heading text-2xl mb-3">Validation en cours côté Stripe</h1>
                        <p className="text-foreground/70 mb-6 leading-relaxed">
                            Votre paiement est en cours de traitement. Vous recevrez un email dans les prochaines minutes
                            avec le lien d'activation de votre espace.
                        </p>
                        <Link to="/"><Button variant="outline">Retour à l'accueil</Button></Link>
                    </>
                )}

                {state.phase === 'error' && (
                    <>
                        <div className="h-16 w-16 rounded-full bg-amber-50 text-amber-600 mx-auto flex items-center justify-center mb-5">
                            <AlertCircle size={32} />
                        </div>
                        <h1 className="font-heading text-2xl mb-3">Vérification impossible</h1>
                        <p className="text-foreground/70 text-sm mb-6">
                            {state.error}<br/>
                            Si vous avez été débité, contactez-nous : <a href="mailto:contact@africanvalley.app" className="text-primary">contact@africanvalley.app</a>
                        </p>
                        <Link to="/"><Button variant="outline">Retour à l'accueil</Button></Link>
                    </>
                )}
            </div>
        </div>
    );
}
